import { useCallback, useEffect, useRef, useState } from 'react';
import { keyframes } from '@mui/material/styles';
import type { ChatCitation, CitationSection } from '@hv/shared';
import { BRAND } from '../theme';

export const CITE_ATTR = 'data-cite';
export const CITE_HIGHLIGHT_ATTR = 'data-cite-highlight';
export const DRAWER_HEADER_ATTR = 'data-drawer-header';

export type CiteAnchor = `applicant:${string}` | `narrative:${string}` | `recommendation:${string}` | `activity:${string}` | CitationSection;

export const citeAnchor = (anchor: CiteAnchor) => ({ [CITE_ATTR]: anchor });

export function anchorsFor(c: ChatCitation): CiteAnchor[] {
  if (!c.available) return [];
  switch (c.kind) {
    case 'applicant':
      return c.target ? [`narrative:${c.target}`, `applicant:${c.target}`] : [];
    case 'recommendation':
      return c.target ? [`recommendation:${c.target}`] : [];
    case 'activity':
      return c.target ? [`activity:${c.target}`] : [];
    default:
      return [c.kind];
  }
}

const HEADER_GAP = 12;

const flash = keyframes`
  0%, 55% { box-shadow: 0 0 0 3px ${BRAND.gold}; }
  100% { box-shadow: 0 0 0 3px transparent; }
`;

export const citeHighlightSx = { [`& [${CITE_HIGHLIGHT_ATTR}]`]: { animation: `${flash} 2.4s ease-out` } };

export function revealCitation(container: HTMLElement, c: ChatCitation): boolean {
  const targets = anchorsFor(c).flatMap((a) => [...container.querySelectorAll<HTMLElement>(`[${CITE_ATTR}="${a}"]`)]);
  const first = targets[0];
  if (!first) return false;
  const header = container.querySelector<HTMLElement>(`[${DRAWER_HEADER_ATTR}]`);
  const clearance = header && header !== first ? header.offsetHeight + HEADER_GAP : 0;
  const top = header === first ? 0 : container.scrollTop + first.getBoundingClientRect().top - container.getBoundingClientRect().top - clearance;
  const smooth = !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  container.scrollTo({ top: Math.max(0, top), behavior: smooth ? 'smooth' : 'auto' });
  for (const el of container.querySelectorAll(`[${CITE_HIGHLIGHT_ATTR}]`)) el.removeAttribute(CITE_HIGHLIGHT_ATTR);
  void container.offsetWidth;
  for (const el of targets) el.setAttribute(CITE_HIGHLIGHT_ATTR, '');
  return true;
}

export function useCitationReveal(householdId: string | null, ready: boolean) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [pending, setPending] = useState<ChatCitation | null>(null);

  useEffect(() => {
    const container = scrollRef.current;
    if (!pending || !ready || !container) return;
    if (!revealCitation(container, pending)) console.warn('Cited source not found in the household drawer', { householdId, kind: pending.kind, target: pending.target });
    setPending(null);
  }, [pending, ready, householdId]);

  const reveal = useCallback((c: ChatCitation) => setPending(c), []);
  return { scrollRef, reveal };
}
